import React, { useEffect, useState, useRef, Children, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ArrowRight, Check, Sparkles, Phone, User, ChevronLeft, ChevronRight, Stethoscope, Award, TrendingUp } from 'lucide-react';
import { TestimonialCard } from './components/TestimonialCard';
import { ThankYouPage } from './components/ThankYouPage';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const slideIn = {
  hidden: {
    opacity: 0,
    x: 100
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: {
    opacity: 0,
    x: -100,
    transition: {
      duration: 0.3
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
// Testimonials data
const testimonials = [{
  quote: 'Finalmente me reconocí en el espejo. El procedimiento fue más sencillo de lo que esperaba y los resultados son exactamente lo que quería: naturales.',
  name: 'María G.',
  location: 'Miami, FL',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
}, {
  quote: 'Después de años de frustración en el gimnasio, DermaClinic trabajó exactamente en las zonas que no respondían. El personal fue increíblemente profesional.',
  name: 'Carmen R.',
  location: 'Houston, TX',
  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
}, {
  quote: 'La recuperación fue muy rápida. Volví al trabajo en dos días. Han pasado 6 meses y mi piel se ve más firme que en mis 20s.',
  name: 'Ana K.',
  location: 'New York, NY',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
}];
// Cities data
const cities = [{
  id: 'miami',
  name: 'Miami',
  state: 'FL'
}, {
  id: 'houston',
  name: 'Houston',
  state: 'TX'
}, {
  id: 'newyork',
  name: 'New York',
  state: 'NY'
}, {
  id: 'lasvegas',
  name: 'Las Vegas',
  state: 'NV'
}, {
  id: 'phoenix',
  name: 'Phoenix',
  state: 'AZ'
}];
// Treatments data
const treatments = [{
  id: 'lipo360',
  name: 'Lipo 360',
  icon: '○',
  desc: 'Contorno completo'
}, {
  id: 'laserlipo',
  name: 'Laser Lipo',
  icon: '◇',
  desc: 'Tecnología avanzada'
}, {
  id: 'bbl',
  name: 'BBL',
  icon: '◐',
  desc: 'Brazilian Butt Lift'
}, {
  id: 'chinlipo',
  name: 'Papada / Chin Lipo',
  icon: '◒',
  desc: 'Definición de mandíbula'
}, {
  id: 'facetightening',
  name: 'Face Tightening',
  icon: '◑',
  desc: 'Rejuvenecimiento facial'
}, {
  id: 'bodycontouring',
  name: 'Body Contouring',
  icon: '◎',
  desc: 'Escultura corporal'
}, {
  id: 'mommymakeover',
  name: 'Mommy Makeover',
  icon: '◈',
  desc: 'Transformación completa'
}];
export function App() {
  const [showThankYou, setShowThankYou] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [direction, setDirection] = useState(0);
  const [formData, setFormData] = useState({
    city: '',
    treatment: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    whatsappConsent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  // Initialize Meta Pixel on component mount
  useEffect(() => {
    // Check if pixel is already loaded
    if (typeof (window as any).fbq !== 'undefined') {
      console.log('Meta Pixel already loaded');
      return;
    }
    // Ensure document is ready
    if (typeof document === 'undefined') {
      console.warn('Document not available, skipping pixel load');
      return;
    }
    try {
      // Load Meta Pixel base code
      ;
      (function (f: any, b: any, e: string, v: string) {
        if (f.fbq) return;
        const n: any = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e);
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        // Safety check: ensure parent node exists
        if (s && s.parentNode) {
          s.parentNode.insertBefore(t, s);
        } else {
          // Fallback: append to head
          b.head.appendChild(t);
        }
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      (window as any).fbq('init', '1329531175115236');
      (window as any).fbq('track', 'PageView');
      console.log('PIXEL_PAGEVIEW_FIRED');
    } catch (error) {
      console.error('Failed to load Meta Pixel (non-blocking):', error);
    }
  }, []);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const nextTestimonial = () => {
    setDirection(1);
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setDirection(-1);
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};
    if (step === 1 && !formData.city) {
      newErrors.city = 'Por favor selecciona una ciudad';
    }
    if (step === 2 && !formData.treatment) {
      newErrors.treatment = 'Por favor selecciona un tratamiento';
    }
    if (step === 3) {
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'El nombre es requerido';
      }
      // Email validation (now REQUIRED)
      if (!formData.email.trim()) {
        newErrors.email = 'El email es requerido';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          newErrors.email = 'Por favor ingresa un email válido';
        }
      }
      // Strict phone validation
      const digits = formData.phoneNumber.replace(/\D/g, '');
      if (digits.length < 10) {
        newErrors.phoneNumber = 'Por favor ingresa un número válido (10 dígitos).';
      }
      if (!formData.whatsappConsent) {
        newErrors.whatsappConsent = 'Debes aceptar para continuar';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 3) {
        handleSubmit();
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };
  const handleSubmit = async () => {
    // Prevent double submission (only during active submission)
    if (isSubmitting) {
      console.log('Submission already in progress, ignoring click');
      return;
    }
    // Disable submit button immediately
    setIsSubmitting(true);
    // Check for duplicate submission within 2 minutes (anti rapid-fire only)
    const submissionKey = `dermaclinic_lead_sent_${formData.email}_${formData.phoneNumber.replace(/\D/g, '')}`;
    const lastSubmitTime = sessionStorage.getItem(submissionKey);
    const now = Date.now();
    const twoMinutes = 2 * 60 * 1000;
    if (lastSubmitTime) {
      const timeSinceLastSubmit = now - parseInt(lastSubmitTime);
      if (timeSinceLastSubmit < twoMinutes) {
        const secondsRemaining = Math.ceil((twoMinutes - timeSinceLastSubmit) / 1000);
        console.log(`Duplicate prevented. ${secondsRemaining} seconds remaining until next send allowed.`);
        // Still redirect to Thank You page (no blocking)
        setShowThankYou(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        setIsSubmitting(false);
        return;
      }
    }
    // Extract UTM parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source') || '';
    const utmMedium = urlParams.get('utm_medium') || '';
    const utmCampaign = urlParams.get('utm_campaign') || '';
    const utmTerm = urlParams.get('utm_term') || '';
    const utmContent = urlParams.get('utm_content') || '';
    // Capture landing page URL at submit time
    const pageUrl = window.location.href;
    // Split full name into first and last
    const nameParts = formData.fullName.trim().split(' ');
    const firstName = nameParts[0] || formData.fullName;
    const lastName = nameParts.slice(1).join(' ') || '';
    // Prepare GHL webhook payload
    const ghlPayload = {
      first_name: firstName,
      last_name: lastName,
      full_name: formData.fullName,
      email: formData.email,
      phone: formData.phoneNumber,
      city: formData.city,
      treatment: formData.treatment,
      source: 'dermaclinic.blog',
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_term: utmTerm,
      utm_content: utmContent,
      page_url: pageUrl,
      timestamp: new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        dateStyle: 'short',
        timeStyle: 'short'
      })
    };
    // Prepare EmailJS data (same info for email notification)
    const emailJsData = {
      full_name: formData.fullName || '',
      email: formData.email || '',
      phone: formData.phoneNumber || '',
      city: formData.city || '',
      treatment: formData.treatment || '',
      whatsapp_consent: formData.whatsappConsent ? 'Yes' : 'No',
      source: 'dermaclinic.blog',
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_term: utmTerm,
      utm_content: utmContent,
      page_url: pageUrl,
      timestamp: new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        dateStyle: 'short',
        timeStyle: 'short'
      })
    };
    console.log('GHL_PAYLOAD', ghlPayload);
    console.log('EMAIL_PAYLOAD', emailJsData);
    console.log('UTM_TRACKING', {
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_term: utmTerm,
      utm_content: utmContent
    });
    let ghlSuccess = false;
    let emailSuccess = false;
    let pixelFired = false;
    try {
      // SEND TO BOTH: GHL + EmailJS in parallel
      const [ghlResponse, emailResponse] = await Promise.allSettled([
      // Send to GoHighLevel
      fetch('https://services.leadconnectorhq.com/hooks/0nuO5M2cKFYDN9cBMlN0/webhook-trigger/9462830b-9cdb-49e0-8715-9393bb976d24', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ghlPayload)
      }),
      // Send to EmailJS (notification)
      fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_id: 'service_otq5unq',
          template_id: 'template_uyui7o6',
          user_id: 'U3N2yFeaznDqrv0X6',
          template_params: emailJsData
        })
      })]);
      // Check GHL result
      if (ghlResponse.status === 'fulfilled' && ghlResponse.value.ok) {
        console.log('✅ GHL_SUCCESS');
        ghlSuccess = true;
      } else {
        console.error('❌ GHL_ERROR', ghlResponse);
      }
      // Check EmailJS result
      if (emailResponse.status === 'fulfilled' && emailResponse.value.ok) {
        console.log('✅ EMAIL_SUCCESS');
        emailSuccess = true;
      } else {
        console.error('❌ EMAIL_ERROR', emailResponse);
      }
      // If at least ONE succeeded, consider it a success
      if (ghlSuccess || emailSuccess) {
        // Mark this submission with current timestamp
        sessionStorage.setItem(submissionKey, now.toString());
        // Save lead data to localStorage
        const leadDataForStorage = {
          fullName: formData.fullName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          city: formData.city,
          treatment: formData.treatment,
          whatsappConsent: formData.whatsappConsent,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem('dermaclinic_lead_data', JSON.stringify(leadDataForStorage));
        // Fire Meta Pixel Lead Event ONLY ONCE
        if (typeof window !== 'undefined' && typeof (window as any).fbq === 'function') {
          try {
            ;
            (window as any).fbq('track', 'Lead', {
              content_name: formData.treatment,
              city: formData.city,
              lead_source: 'dermaclinic.blog'
            });
            console.log('PIXEL_LEAD_FIRED', {
              treatment: formData.treatment,
              city: formData.city
            });
            pixelFired = true;
          } catch (error) {
            console.warn('Meta Pixel Lead event failed (non-blocking):', error);
          }
        }
        // Redirect to Thank You page
        setShowThankYou(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        // Both failed - show error
        console.error('❌ BOTH_FAILED');
        alert('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      console.error('❌ SUBMISSION_ERROR', error);
      alert('Hubo un error al enviar tu solicitud. Por favor verifica tu conexión e intenta de nuevo.');
      setIsSubmitting(false);
      return;
    } finally {
      // Re-enable button after 3 seconds if at least one succeeded
      if (ghlSuccess || emailSuccess) {
        setTimeout(() => {
          setIsSubmitting(false);
        }, 3000);
      }
    }
  };
  if (showThankYou) {
    return <ThankYouPage data-id="element-0" />;
  }
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#FEFDFB] font-sans text-[#2E2E2E] antialiased" data-id="element-1">
      {/* Minimal Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E6D3B3]/20" data-id="element-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between" data-id="element-3">
          <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-9 sm:h-10 w-auto" data-id="element-4" />
        </div>
      </header>

      {/* Hero Section - Enhanced with Premium Background */}
      <section className="relative min-h-[65vh] flex items-center justify-center px-4 sm:px-6 pt-24 pb-20 sm:pb-24 overflow-hidden" data-id="element-5">
        {/* Premium Gradient Background with Texture */}
        <div className="absolute inset-0" data-id="element-6">
          <div className="absolute inset-0 bg-gradient-to-br from-[#F9F7F2] via-[#FEFDFB] to-[#F5F1E8]" data-id="element-7" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A961' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} data-id="element-8" />
          {/* Subtle Radial Glow */}
          <div className="absolute inset-0 bg-gradient-radial from-[#C9A961]/5 via-transparent to-transparent" data-id="element-9" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-10">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-11">
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.1] mb-6 text-[#2E2E2E]" data-id="element-12">
              Redefine tu Figura. <br data-id="element-13" />
              <span className="text-[#C9A961] font-normal" data-id="element-14">
                Recupera tu Confianza.
              </span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-base sm:text-lg md:text-xl text-[#555555] mb-6 leading-relaxed max-w-2xl mx-auto font-light" data-id="element-15">
              Tratamientos avanzados de contorno corporal con mínima
              recuperación y resultados naturales.
            </motion.p>

            {/* Microcopy Trust Signals */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs sm:text-sm text-[#888888] mb-8" data-id="element-16">
              <div className="flex items-center gap-1.5" data-id="element-17">
                <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-18" />
                <span data-id="element-19">Consulta gratuita</span>
              </div>
              <span className="text-[#E6D3B3] hidden sm:inline" data-id="element-20">•</span>
              <div className="flex items-center gap-1.5" data-id="element-21">
                <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-22" />
                <span data-id="element-23">Sin compromiso</span>
              </div>
              <span className="text-[#E6D3B3] hidden sm:inline" data-id="element-24">•</span>
              <div className="flex items-center gap-1.5" data-id="element-25">
                <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-26" />
                <span data-id="element-27">Respuesta en 24h</span>
              </div>
            </motion.div>

            <motion.button variants={fadeInUp} onClick={scrollToForm} className="px-8 py-4 bg-[#2E2E2E] text-white rounded-2xl font-semibold hover:bg-[#1F1F1F] transition-all shadow-lg hover:shadow-xl active:scale-[0.98] text-base sm:text-lg" data-id="element-28">
              Agenda tu Consulta Gratuita Ahora
            </motion.button>
          </motion.div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E6D3B3]/30 to-transparent" data-id="element-29" />
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white" data-id="element-30">
        <div className="max-w-7xl mx-auto" data-id="element-31">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12 sm:mb-16" data-id="element-32">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-4 text-[#2E2E2E]" data-id="element-33">
              ¿Por Qué Elegir DermaClinic?
            </h2>
            <p className="text-[#888888] text-base sm:text-lg" data-id="element-34">
              Tecnología médica avanzada combinada con atención personalizada
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-35">
            {[{
            icon: Sparkles,
            title: 'Tecnología Láser Avanzada',
            desc: 'Equipos de última generación'
          }, {
            icon: Stethoscope,
            title: 'Procedimientos Mínimamente Invasivos',
            desc: 'Recuperación rápida'
          }, {
            icon: Award,
            title: 'Atención Médica Especializada',
            desc: 'Especialistas certificados'
          }, {
            icon: TrendingUp,
            title: 'Resultados Personalizados',
            desc: 'Adaptados a tu cuerpo'
          }].map((benefit, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1,
            duration: 0.5
          }} className="flex flex-col items-center text-center p-6 sm:p-8 bg-[#F9F7F2] rounded-2xl border border-[#E6D3B3]/50 hover:border-[#C9A961]/30 transition-colors" data-id="element-36">
                <div className="w-14 h-14 rounded-full bg-white border border-[#E6D3B3] flex items-center justify-center mb-4" data-id="element-37">
                  <benefit.icon className="w-7 h-7 text-[#C9A961]" strokeWidth={1.5} data-id="element-38" />
                </div>
                <h3 className="text-base font-semibold mb-2 text-[#2E2E2E]" data-id="element-39">
                  {benefit.title}
                </h3>
                <p className="text-sm text-[#888888] leading-relaxed" data-id="element-40">
                  {benefit.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Consultation Wizard - Enhanced with Intro Text */}
      <section ref={formRef} className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-[#F9F7F2] scroll-mt-20" data-id="element-41">
        <div className="max-w-3xl mx-auto" data-id="element-42">
          {/* Form Introduction */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10 sm:mb-12" data-id="element-43">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-4 text-[#2E2E2E]" data-id="element-44">
              Comienza tu Transformación
            </h2>
            <p className="text-[#555555] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-45">
              Completa tus datos y una especialista te contactará en menos de 24
              horas para coordinar tu consulta gratuita.
            </p>
          </motion.div>

          {/* Progress Bar */}
          <div className="mb-8 sm:mb-12" data-id="element-46">
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-47">
              {[1, 2, 3].map(step => <div key={step} className={`h-1 rounded-full transition-all duration-500 ${step <= currentStep ? 'w-16 bg-[#C9A961]' : 'w-8 bg-[#E6D3B3]'}`} data-id="element-48" />)}
            </div>
            <p className="text-center text-sm text-[#888888]" data-id="element-49">
              Paso {currentStep} de 3
            </p>
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait" data-id="element-50">
            {currentStep === 1 && <motion.div key="step1" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="space-y-8" data-id="element-51">
                <div className="text-center mb-8" data-id="element-52">
                  <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light mb-3 text-[#2E2E2E]" data-id="element-53">
                    Paso 1: ¿Dónde te encuentras?
                  </h3>
                  <p className="text-[#888888] text-sm sm:text-base" data-id="element-54">
                    Selecciona tu ciudad para encontrar la clínica más cercana
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-55">
                  {cities.map(city => <button key={city.id} onClick={() => setFormData({
                ...formData,
                city: city.name
              })} className={`group relative p-6 rounded-2xl border-2 transition-all text-left ${formData.city === city.name ? 'border-[#C9A961] bg-white shadow-[0_0_0_4px_rgba(201,169,97,0.1)]' : 'border-[#E6D3B3] hover:border-[#C9A961]/50 bg-white'}`} data-id="element-56">
                      <div className="flex items-center justify-between" data-id="element-57">
                        <div data-id="element-58">
                          <div className="flex items-center gap-2 mb-1" data-id="element-59">
                            <MapPin className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} data-id="element-60" />
                            <h4 className="font-semibold text-lg text-[#2E2E2E]" data-id="element-61">
                              {city.name}
                            </h4>
                          </div>
                          <p className="text-sm text-[#888888]" data-id="element-62">{city.state}</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${formData.city === city.name ? 'border-[#C9A961] bg-[#C9A961]' : 'border-[#E6D3B3]'}`} data-id="element-63">
                          {formData.city === city.name && <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-64" />}
                        </div>
                      </div>
                    </button>)}
                </div>

                {errors.city && <p className="text-sm text-red-500 text-center" data-id="element-65">
                    {errors.city}
                  </p>}
              </motion.div>}

            {currentStep === 2 && <motion.div key="step2" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="space-y-8" data-id="element-66">
                <div className="text-center mb-8" data-id="element-67">
                  <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light mb-3 text-[#2E2E2E]" data-id="element-68">
                    Paso 2: ¿Qué área quieres transformar?
                  </h3>
                  <p className="text-[#888888] text-sm sm:text-base" data-id="element-69">
                    Elige el tratamiento que mejor se adapte a tus objetivos
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-70">
                  {treatments.map(treatment => <button key={treatment.id} onClick={() => setFormData({
                ...formData,
                treatment: treatment.name
              })} className={`group relative p-6 rounded-2xl border-2 transition-all text-left ${formData.treatment === treatment.name ? 'border-[#C9A961] bg-white shadow-[0_0_0_4px_rgba(201,169,97,0.1)]' : 'border-[#E6D3B3] hover:border-[#C9A961]/50 bg-white'}`} data-id="element-71">
                      <div className="flex items-start justify-between mb-3" data-id="element-72">
                        <div className="w-12 h-12 rounded-full bg-[#F9F7F2] border border-[#E6D3B3] flex items-center justify-center text-2xl text-[#C9A961]" data-id="element-73">
                          {treatment.icon}
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${formData.treatment === treatment.name ? 'border-[#C9A961] bg-[#C9A961]' : 'border-[#E6D3B3]'}`} data-id="element-74">
                          {formData.treatment === treatment.name && <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-75" />}
                        </div>
                      </div>
                      <h4 className="font-semibold text-base text-[#2E2E2E] mb-1" data-id="element-76">
                        {treatment.name}
                      </h4>
                      <p className="text-sm text-[#888888]" data-id="element-77">{treatment.desc}</p>
                    </button>)}
                </div>

                {errors.treatment && <p className="text-sm text-red-500 text-center" data-id="element-78">
                    {errors.treatment}
                  </p>}
              </motion.div>}

            {currentStep === 3 && <motion.div key="step3" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="space-y-8" data-id="element-79">
                <div className="text-center mb-8" data-id="element-80">
                  <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light mb-3 text-[#2E2E2E]" data-id="element-81">
                    Paso 3: ¡Último paso para tu consulta gratuita!
                  </h3>
                  <p className="text-[#888888] text-sm sm:text-base" data-id="element-82">
                    Déjanos tus datos y te contactaremos en menos de 24 horas
                    para coordinar tu cita
                  </p>
                </div>

                <div className="space-y-5" data-id="element-83">
                  {/* Full Name */}
                  <div data-id="element-84">
                    <label className="block text-sm font-medium text-[#2E2E2E] mb-2" data-id="element-85">
                      Nombre Completo
                    </label>
                    <div className="relative" data-id="element-86">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" strokeWidth={1.5} data-id="element-87" />
                      <input type="text" value={formData.fullName} onChange={e => setFormData({
                    ...formData,
                    fullName: e.target.value
                  })} placeholder="Tu nombre completo" className={`w-full pl-12 pr-4 py-4 text-base bg-white border-2 rounded-2xl text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#C9A961] transition-all ${errors.fullName ? 'border-red-400' : 'border-[#E6D3B3]'}`} style={{
                    fontSize: '16px'
                  }} data-id="element-88" />
                    </div>
                    {errors.fullName && <p className="text-xs text-red-500 mt-1.5" data-id="element-89">
                        {errors.fullName}
                      </p>}
                  </div>

                  {/* Email (Required) */}
                  <div data-id="element-90">
                    <label className="block text-sm font-medium text-[#2E2E2E] mb-2" data-id="element-91">
                      Email
                    </label>
                    <div className="relative" data-id="element-92">
                      <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} data-id="element-93">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" data-id="element-94" />
                      </svg>
                      <input type="email" inputMode="email" autoComplete="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} placeholder="tu@email.com" className={`w-full pl-12 pr-4 py-4 text-base bg-white border-2 rounded-2xl text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#C9A961] transition-all ${errors.email ? 'border-red-400' : 'border-[#E6D3B3]'}`} style={{
                    fontSize: '16px'
                  }} data-id="element-95" />
                    </div>
                    {errors.email && <p className="text-xs text-red-500 mt-1.5" data-id="element-96">
                        {errors.email}
                      </p>}
                  </div>

                  {/* Phone Number */}
                  <div data-id="element-97">
                    <label className="block text-sm font-medium text-[#2E2E2E] mb-2" data-id="element-98">
                      Teléfono
                    </label>
                    <div className="relative" data-id="element-99">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" strokeWidth={1.5} data-id="element-100" />
                      <input type="tel" inputMode="numeric" autoComplete="tel" value={formData.phoneNumber} onChange={e => setFormData({
                    ...formData,
                    phoneNumber: e.target.value
                  })} placeholder="Tu número de teléfono" className={`w-full pl-12 pr-4 py-4 text-base bg-white border-2 rounded-2xl text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#C9A961] transition-all ${errors.phoneNumber ? 'border-red-400' : 'border-[#E6D3B3]'}`} style={{
                    fontSize: '16px'
                  }} data-id="element-101" />
                    </div>
                    {errors.phoneNumber && <p className="text-xs text-red-500 mt-1.5" data-id="element-102">
                        {errors.phoneNumber}
                      </p>}
                  </div>

                  {/* WhatsApp Consent */}
                  <div className="pt-2" data-id="element-103">
                    <label className="flex items-start gap-3 cursor-pointer group" data-id="element-104">
                      <div className="relative flex-shrink-0 mt-0.5" data-id="element-105">
                        <input type="checkbox" checked={formData.whatsappConsent} onChange={e => setFormData({
                      ...formData,
                      whatsappConsent: e.target.checked
                    })} className="sr-only" data-id="element-106" />
                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.whatsappConsent ? 'bg-[#C9A961] border-[#C9A961]' : errors.whatsappConsent ? 'border-red-400' : 'border-[#E6D3B3] group-hover:border-[#C9A961]'}`} data-id="element-107">
                          {formData.whatsappConsent && <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-108" />}
                        </div>
                      </div>
                      <span className="text-sm text-[#555555] leading-relaxed" data-id="element-109">
                        Acepto recibir confirmaciones y recordatorios por
                        WhatsApp para coordinar mi consulta gratuita.
                      </span>
                    </label>
                    {errors.whatsappConsent && <p className="text-xs text-red-500 mt-1.5" data-id="element-110">
                        {errors.whatsappConsent}
                      </p>}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="mt-8 sm:mt-12 flex items-center gap-3" data-id="element-111">
            {currentStep > 1 && <button onClick={handleBack} className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-[#E6D3B3] text-[#2E2E2E] rounded-2xl font-medium hover:border-[#C9A961] transition-all" data-id="element-112">
                <ChevronLeft className="w-5 h-5" strokeWidth={2} data-id="element-113" />
                Atrás
              </button>}

            <button onClick={handleNext} disabled={isSubmitting} className={`flex-1 flex items-center justify-center gap-2 px-8 py-4 bg-[#2E2E2E] text-white rounded-2xl font-semibold hover:bg-[#1F1F1F] transition-all shadow-lg hover:shadow-xl active:scale-[0.98] ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-114">
              {currentStep === 3 ? isSubmitting ? 'Enviando tu solicitud...' : 'Confirmar mi Consulta Gratuita' : 'Continuar →'}
              {currentStep !== 3 && <ArrowRight className="w-5 h-5" strokeWidth={2} data-id="element-115" />}
            </button>
          </div>

          {currentStep === 3 && <p className="text-xs text-center text-[#888888] mt-4 leading-relaxed" data-id="element-116">
              🔒 Tus datos están protegidos. Al confirmar, aceptas que un
              especialista médico te contacte para coordinar tu evaluación
              gratuita sin compromiso.
            </p>}

          {/* Trust Logos - Financing */}
          {currentStep === 3 && <div className="mt-8 pt-8 border-t border-[#E6D3B3]/50" data-id="element-117">
              <p className="text-xs text-center text-[#888888] mb-4" data-id="element-118">
                Opciones de financiamiento disponibles
              </p>
              <div className="flex items-center justify-center gap-8" data-id="element-119">
                <img src="https://cdn.magicpatterns.com/uploads/mbjD9Zum9t5ZWvjctvX8GS/main_logo.png" alt="CareCredit" className="h-6 object-contain" data-id="element-120" />
                <img src="https://cdn.magicpatterns.com/uploads/kDSEimj5qpeQvJ4aHi6GSi/Screenshot_2026-01-17_at_9.28.56_PM.png" alt="Affirm" className="h-6 object-contain" data-id="element-121" />
                <img src="https://cdn.magicpatterns.com/uploads/88bDZo8Hcqa4d9fVGJ6R2k/Alphaeon_Credit_logo_registered_RGB.webp" alt="Alphaeon" className="h-6 object-contain" data-id="element-122" />
              </div>
            </div>}
        </div>
      </section>

      {/* Testimonials Section - Enhanced Cards */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#F9F7F2]" data-id="element-123">
        <div className="max-w-7xl mx-auto" data-id="element-124">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12 sm:mb-16" data-id="element-125">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-4 text-[#2E2E2E]" data-id="element-126">
              Historias Reales
            </h2>
            <p className="text-[#888888] text-base sm:text-lg" data-id="element-127">
              Miles de pacientes han transformado su figura con nosotros
            </p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto" data-id="element-128">
            <div className="relative overflow-hidden rounded-2xl" data-id="element-129">
              <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-130">
                <motion.div key={currentTestimonial} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                x: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                },
                opacity: {
                  duration: 0.2
                }
              }} data-id="element-131">
                  <TestimonialCard quote={testimonials[currentTestimonial].quote} name={testimonials[currentTestimonial].name} location={testimonials[currentTestimonial].location} image={testimonials[currentTestimonial].image} delay={0} data-id="element-132" />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-center gap-4 mt-8" data-id="element-133">
              <button onClick={prevTestimonial} className="w-12 h-12 rounded-full bg-white border border-[#E6D3B3] flex items-center justify-center hover:border-[#C9A961] transition-all" data-id="element-134">
                <ChevronLeft className="w-5 h-5 text-[#2E2E2E]" strokeWidth={2} data-id="element-135" />
              </button>

              <div className="flex items-center gap-2" data-id="element-136">
                {testimonials.map((_, idx) => <button key={idx} onClick={() => {
                setDirection(idx > currentTestimonial ? 1 : -1);
                setCurrentTestimonial(idx);
              }} className={`transition-all rounded-full ${idx === currentTestimonial ? 'w-8 h-2 bg-[#C9A961]' : 'w-2 h-2 bg-[#E6D3B3] hover:bg-[#C9A961]/50'}`} data-id="element-137" />)}
              </div>

              <button onClick={nextTestimonial} className="w-12 h-12 rounded-full bg-white border border-[#E6D3B3] flex items-center justify-center hover:border-[#C9A961] transition-all" data-id="element-138">
                <ChevronRight className="w-5 h-5 text-[#2E2E28]" strokeWidth={2} data-id="element-139" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white" data-id="element-140">
        <div className="max-w-6xl mx-auto" data-id="element-141">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12 sm:mb-16" data-id="element-142">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mb-4 text-[#2E2E2E]" data-id="element-143">
              Opciones de Financiamiento
            </h2>
            <p className="text-[#888888] text-base sm:text-lg" data-id="element-144">
              Alternativas flexibles para pacientes calificados
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8 mb-8" data-id="element-145">
            {[{
            name: 'CareCredit',
            logo: 'https://cdn.magicpatterns.com/uploads/mbjD9Zum9t5ZWvjctvX8GS/main_logo.png',
            desc: 'Financiamiento médico con planes de pago flexibles y períodos promocionales.'
          }, {
            name: 'Affirm',
            logo: 'https://cdn.magicpatterns.com/uploads/kDSEimj5qpeQvJ4aHi6GSi/Screenshot_2026-01-17_at_9.28.56_PM.png',
            desc: 'Pagos mensuales simples con términos transparentes y sin cargos ocultos.'
          }, {
            name: 'Alphaeon Credit',
            logo: 'https://cdn.magicpatterns.com/uploads/88bDZo8Hcqa4d9fVGJ6R2k/Alphaeon_Credit_logo_registered_RGB.webp',
            desc: 'Crédito especializado para procedimientos estéticos y médicos con opciones flexibles.'
          }].map((provider, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1,
            duration: 0.5
          }} className="bg-[#F9F7F2] p-8 rounded-2xl border border-[#E6D3B3]/50 hover:border-[#C9A961]/30 transition-colors" data-id="element-146">
                <div className="flex items-center justify-center mb-6 h-16" data-id="element-147">
                  <img src={provider.logo} alt={provider.name} className="h-12 w-auto object-contain" data-id="element-148" />
                </div>
                <p className="text-[#555555] leading-relaxed text-center text-sm mb-6" data-id="element-149">
                  {provider.desc}
                </p>
                <p className="text-xs text-[#888888] text-center italic mb-6" data-id="element-150">
                  Opciones flexibles disponibles para pacientes calificados
                </p>

                <button onClick={scrollToForm} className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#2E2E2E] text-white rounded-xl font-semibold hover:bg-[#1F1F1F] transition-all shadow-sm hover:shadow-md active:scale-[0.98]" data-id="element-151">
                  Consultar Opciones de Financiamiento
                  <ArrowRight className="w-4 h-4" strokeWidth={2} data-id="element-152" />
                </button>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center p-6 bg-[#F9F7F2] rounded-2xl border border-[#E6D3B3]/50" data-id="element-153">
            <p className="text-sm text-[#555555]" data-id="element-154">
              Nuestro equipo te ayudará a explorar el mejor plan de pago durante
              tu consulta gratuita.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 bg-white border-t border-[#E6D3B3]/30" data-id="element-155">
        <div className="max-w-7xl mx-auto" data-id="element-156">
          <p className="text-xs text-[#888888] text-center mb-4 leading-relaxed" data-id="element-157">
            Los resultados individuales varían. Este contenido es educativo y no
            sustituye el consejo médico.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-158">
            <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-8 w-auto opacity-60" data-id="element-159" />
            <p className="text-xs text-[#888888] text-center" data-id="element-160">
              © 2024 DermaClinic Center. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky CTA Button - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-gradient-to-t from-white via-white to-transparent md:hidden" data-id="element-161">
        <button onClick={scrollToForm} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#2E2E2E] text-white rounded-2xl font-semibold hover:bg-[#1F1F1F] transition-all shadow-lg active:scale-[0.98]" data-id="element-162">
          Agenda tu Consulta Gratuita Ahora
          <ArrowRight className="w-5 h-5" strokeWidth={2} data-id="element-163" />
        </button>
      </div>
    </div>;
}